import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/components/theme-provider';
import { CookieBanner } from '@/components/CookieBanner';
import { initAnalytics } from '@/lib/analytics';
import App from './App';

// Self-hosted fonts (DSGVO-konform, kein IP-Transfer an Google)
import '@fontsource-variable/outfit';
import '@fontsource-variable/inter';
import '@fontsource-variable/montserrat';
import '@fontsource-variable/playfair-display';
import '@fontsource-variable/raleway';

import './index.css';

initAnalytics();

// ============================================================================
// Capture Referral Code BEFORE React renders (synchronous)
// This ensures we capture the ref parameter before any redirects happen
// ============================================================================
(function captureUrlParams() {
  try {
    const urlParams = new URLSearchParams(window.location.search);

    // Capture referral code (?ref=XXXXX)
    const refId = urlParams.get('ref');
    if (refId && refId.trim()) {
      const codeRegex = /^[A-Za-z0-9]{5}$/;
      if (codeRegex.test(refId)) {
        localStorage.setItem('referrer_id', refId.toUpperCase());
        console.log('[Affiliate] Referral code captured:', refId.toUpperCase());
      } else {
        console.warn('[Affiliate] Invalid referral code format:', refId);
      }
    }
  } catch (e) {
    console.error('[Params] Error capturing URL params:', e);
  }
})();

// Self-heal stale lazy-chunk failures after a deploy: when a dynamic import()
// can't fetch its chunk (its hash changed since this tab loaded the app), Vite
// fires `vite:preloadError`. Reload once to pick up the fresh index.html + chunk
// manifest instead of showing "Failed to fetch dynamically imported module".
// A short time-gated guard prevents a reload loop if the chunk is truly gone.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'vite:preloadError:lastReload';
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return; // already reloaded recently — avoid a loop
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault(); // suppress the unhandled error; we handle it by reloading
  window.location.reload();
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <AuthProvider>
        <App />
        <CookieBanner />
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>
);
